import { forwardRef } from "react";

import { ThemedBidBadge } from "./ThemedBidBadge";
import { ThemedBidStatus } from "./ThemedBidStatus";
import { ThemedEarlyBirdChip } from "./ThemedEarlyBirdChip";
import { ThemedGroupTicketCaption } from "./ThemedGroupTicketCaption";
import { ThemedItemBlockLayout } from "./ThemedItemBlockLayout";
import { ThemedItemPrice } from "./ThemedItemPrice";
import { ThemedMembershipRenewalInfo } from "./ThemedMembershipRenewalInfo";
import { ThemedRemainingItemsInfo } from "./ThemedRemainingItemsInfo";
import { ThemedItemBlockProps } from "./types";
import { useThemedItemBlock } from "./useThemedItemBlock";

import { ThemedItemDescription } from "@/features/FormV2/sharedComponents/ThemedItemBlock/ThemedItemDescription";
import { ThemedItemTitle } from "@/features/FormV2/sharedComponents/ThemedItemBlock/ThemedItemTitle";
import { ThemedResponsiveMultipleImageDisplay } from "@/features/FormV2/sharedComponents/ThemedItemBlock/ThemedMultipleImageDisplay/ThemedResponsiveMultipleImageDisplay";

export const ThemedItemBlock = forwardRef<HTMLDivElement, ThemedItemBlockProps>(
  (
    { id, rate, active, isSoldOut, highlightEarlyBirdStatus, callToAction, additionalQuestions, amountInput, ...rest },
    ref
  ) => {
    const {
      imageDisplay,
      title,
      description,
      price,
      priceCaption,
      bidBadge,
      remainingItemsInfo,
      renewalInfo,
      groupTicketCaption,
    } = useThemedItemBlock({ rate, ...rest });

    return (
      <ThemedItemBlockLayout
        ref={ref}
        id={id}
        active={active}
        isSoldOut={isSoldOut}
        highlightEarlyBirdStatus={highlightEarlyBirdStatus}
        slots={{
          image: imageDisplay ? <ThemedResponsiveMultipleImageDisplay {...imageDisplay} /> : undefined,
          title: <ThemedItemTitle {...title} />, // required
          description: description && <ThemedItemDescription active={active} description={description} />,
          price: <ThemedItemPrice {...price} />, // required
          priceCaption: priceCaption ? <ThemedBidStatus {...priceCaption} /> : undefined,
          earlyBirdInfo: rate.earlyBirdCloseDate ? (
            <ThemedEarlyBirdChip closeDate={rate.earlyBirdCloseDate} />
          ) : undefined,
          bidBadge: bidBadge ? <ThemedBidBadge {...bidBadge} /> : undefined,
          remainingItemsInfo: remainingItemsInfo ? <ThemedRemainingItemsInfo {...remainingItemsInfo} /> : undefined,
          renewalInfo: renewalInfo ? <ThemedMembershipRenewalInfo {...renewalInfo} /> : undefined,
          callToAction: callToAction?.node,
          amountInput,
          additionalQuestions: additionalQuestions?.node,
          groupTicketCaption: groupTicketCaption ? <ThemedGroupTicketCaption {...groupTicketCaption} /> : undefined,
        }}
      />
    );
  }
);

ThemedItemBlock.displayName = "ThemedItemBlock";
